import { RdvTypeValues, Transforms } from "cadendar-shared";
import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useHoraires } from "../../horaires/hooks/useHoraires.ts";
import { usePreferences } from "../../preferences/hooks/usePreferences.ts";
import useApptsByDateWindow from "./useApptsByDateWindow.ts";
import { AppointmentsDTO, RemindersDTO } from "cadendar-trpc-server";

export type EditRdvState = AppointmentsDTO & {
  reminderChecked: boolean;
  confirmChecked: boolean;
  name: string;
  forename: string;
  object: string;
} & {
  type: RdvTypeValues;
};

const getInitialState = (
  rdv: AppointmentsDTO & {
    type: RdvTypeValues;
  },
  reminders: RemindersDTO["reminders"],
): EditRdvState => {
  return {
    ...rdv,
    reminderChecked:
      reminders.filter((r) => r.mode === "sms" || r.mode === "email").length >
      0,
    confirmChecked: !!rdv.confirmation_sent_at,
    name: rdv.name || "",
    forename: rdv.forename || "",
    object: rdv.object || "",
  };
};

const useEditRdv = (
  rdvState:
    | (AppointmentsDTO & {
        type: RdvTypeValues;
      })
    | null,
  reminders: RemindersDTO["reminders"],
  date: Date,
) => {
  const [tempRdv, setTempRdv] = useState(
    (rdvState && getInitialState(rdvState, reminders)) || null,
  );

  useEffect(() => {
    if (!rdvState) return;
    setTempRdv(getInitialState(rdvState, reminders));
  }, [JSON.stringify(rdvState)]);
  const horaire = useHoraires(tempRdv && tempRdv.date);
  const allRdvs = useApptsByDateWindow(
    date,
    DateTime.fromJSDate(date).plus({ days: 1 }).toJSDate(),
  );
  const prefs = usePreferences();
  const onSetHM = ({ hour, minute }: { hour: number; minute: number }) => {
    setTempRdv((tempRdv) => {
      if (!tempRdv) return null;
      const dureesPossibles =
        horaire &&
        prefs &&
        tempRdv &&
        Transforms.rdvs.getDurees2(
          horaire,
          allRdvs,
          tempRdv.date,
          { hour, minute },
          prefs.duree_min_rdv,
          tempRdv,
        );
      const choosenDuree =
        (dureesPossibles &&
          (dureesPossibles.includes(tempRdv.duration)
            ? tempRdv.duration
            : dureesPossibles[0])) ||
        0;
      return { ...tempRdv, hour, minute, duration: choosenDuree };
    });
  };

  const onSetDuree = (duree: number) => {
    setTempRdv((tempRdv) => (tempRdv ? { ...tempRdv, duration: duree } : null));
  };
  const onSetType = (type: RdvTypeValues) => {
    setTempRdv((tempRdv) => (tempRdv ? { ...tempRdv, type } : null));
  };
  const onSetObjet = (objet: string) => {
    setTempRdv((tempRdv) => (tempRdv ? { ...tempRdv, object: objet } : null));
  };
  const onToggleReminder = () => {
    setTempRdv((tempRdv) =>
      tempRdv
        ? {
            ...tempRdv,
            reminderChecked: !tempRdv.reminderChecked,
          }
        : null,
    );
  };
  const onToggleConfirm = () => {
    setTempRdv((tempRdv) =>
      tempRdv
        ? {
            ...tempRdv,
            confirmChecked: !tempRdv.confirmChecked,
          }
        : null,
    );
  };
  const hmPossibles =
    horaire &&
    prefs &&
    rdvState &&
    Transforms.rdvs.hmAvailableWithDuration(
      horaire,
      allRdvs,
      rdvState.date,
      prefs.duree_min_rdv,
      (tempRdv && tempRdv.duration) || rdvState.duration,
      tempRdv || rdvState,
    );
  const dureesPossibles =
    (tempRdv &&
      prefs &&
      horaire &&
      Transforms.rdvs.getDurees2(
        horaire,
        allRdvs,
        date,
        { hour: tempRdv.hour, minute: tempRdv.minute },
        prefs.duree_min_rdv,
        tempRdv,
      )) ||
    [];
  return {
    tempRdv,
    hmPossibles,
    dureesPossibles,
    onSetHM,
    onSetDuree,
    onSetType,
    onSetObjet,
    onToggleReminder,
    onToggleConfirm,
  };
};

export { useEditRdv };
